import { computed, readonly, ref, watch, onUnmounted, onMounted } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import VoiceAPI from 'dashboard/api/channel/voice/voiceAPIClient';
import TwilioVoiceClient from 'dashboard/api/channel/voice/twilioVoiceClient';
import RuutVoiceClient from 'dashboard/api/channel/voice/ruutVoiceClient';
import { useCallsStore } from 'dashboard/stores/calls';
import { useAlert } from 'dashboard/composables';
import {
  useWhatsappCallSession,
  sendWhatsappTerminateBeacon,
  cleanupWhatsappSession,
  teardownDataChannelBridge,
  armOutboundRecorder,
} from 'dashboard/composables/useWhatsappCallSession';
import {
  handleVoiceCallCreated,
  markCallDismissed,
  markLocalCall,
  clearLocalCall,
  dismissedCallSids,
} from 'dashboard/helper/voice';
import { VOICE_CALL_PROVIDERS } from 'dashboard/helper/inbox';
import {
  CONTENT_TYPES,
  VOICE_CALL_DIRECTION,
  VOICE_CALL_STATUS,
} from 'dashboard/components-next/message/constants';
import Timer from 'dashboard/helper/Timer';
import { getCallTabCoordinator } from 'dashboard/helper/callTabCoordinator';

const callTabCoordinator = getCallTabCoordinator();

const isWhatsappCall = call => call?.provider === VOICE_CALL_PROVIDERS.WHATSAPP;
const isRuutVoiceCall = call =>
  call?.provider === VOICE_CALL_PROVIDERS.RUUT_VOICE;
const voiceClientFor = call =>
  isRuutVoiceCall(call) ? RuutVoiceClient : TwilioVoiceClient;

// Globals attached once across all useCallSession() consumers — bubbles in a
// long thread call this composable many times, and a per-instance Timer +
// window listener stack would multiply work.
let globalsAttachedCount = 0;
let globalDurationTimer = null;
const globalCallDuration = ref(0);
const globalCallTimerRunning = ref(false);
let storedCallsStoreRef = null;
let reconcileActiveCallsRef = null;
let disconnectReconcileTimer = null;
// The call the running timer belongs to, so a late in-progress update for a
// call whose timer already started at join doesn't reset the elapsed time.
let timerStartedForCallSid = null;
// Shared join lock so two surfaces (bubble + widget) clicking concurrently
// see one in-flight join, not two unrelated isJoining refs.
const globalIsJoining = ref(false);
const globalIsJoiningReadonly = readonly(globalIsJoining);

const handleBeforeUnloadGlobal = event => {
  const store = storedCallsStoreRef;
  if (!store) return;
  if (!store.hasActiveCall && !store.hasIncomingCall) return;
  event.preventDefault();
  event.returnValue = '';
};
const handlePageHideGlobal = () => {
  sendWhatsappTerminateBeacon();
  callTabCoordinator.releaseAll();
};
const handleProviderDisconnectedGlobal = () => {
  window.clearTimeout(disconnectReconcileTimer);
  // Provider status callbacks and cable events can arrive just after the SDK
  // reports a closed leg. Let the server settle before deciding to recover.
  disconnectReconcileTimer = window.setTimeout(
    () => reconcileActiveCallsRef?.(),
    1500
  );
};
const handleVisibilityChangeGlobal = () => {
  if (document.visibilityState === 'visible') reconcileActiveCallsRef?.();
};
const handleProviderReconnectingGlobal = () =>
  storedCallsStoreRef?.setActiveConnectionState('reconnecting');
const handleProviderReconnectedGlobal = () =>
  storedCallsStoreRef?.setActiveConnectionState('connected');

// The duration timer measures talk time, not ring time. Inbound and WhatsApp
// calls are answered the moment the agent joins, so their timer starts at
// join; outbound calls start counting when the callee's answer arrives as an
// in-progress status update (see the activeCall watcher below).
const startDurationTimer = call => {
  if (!call || timerStartedForCallSid === call.callSid) return;
  timerStartedForCallSid = call.callSid;
  globalCallTimerRunning.value = true;
  globalDurationTimer?.start();
};
const stopDurationTimer = () => {
  timerStartedForCallSid = null;
  globalCallTimerRunning.value = false;
  globalDurationTimer?.stop();
  globalCallDuration.value = 0;
};

const attachGlobalsOnFirstMount = callsStore => {
  globalsAttachedCount += 1;
  if (globalsAttachedCount > 1) return;
  storedCallsStoreRef = callsStore;
  globalDurationTimer = new Timer(elapsed => {
    globalCallDuration.value = elapsed;
  });
  TwilioVoiceClient.addEventListener(
    'call:disconnected',
    handleProviderDisconnectedGlobal
  );
  RuutVoiceClient.addEventListener(
    'call:disconnected',
    handleProviderDisconnectedGlobal
  );
  TwilioVoiceClient.addEventListener(
    'call:reconnecting',
    handleProviderReconnectingGlobal
  );
  TwilioVoiceClient.addEventListener(
    'call:reconnected',
    handleProviderReconnectedGlobal
  );
  RuutVoiceClient.addEventListener(
    'call:reconnecting',
    handleProviderReconnectingGlobal
  );
  RuutVoiceClient.addEventListener(
    'call:reconnected',
    handleProviderReconnectedGlobal
  );
  window.addEventListener('beforeunload', handleBeforeUnloadGlobal);
  window.addEventListener('pagehide', handlePageHideGlobal);
  document.addEventListener('visibilitychange', handleVisibilityChangeGlobal);
};

const detachGlobalsOnLastUnmount = () => {
  globalsAttachedCount -= 1;
  if (globalsAttachedCount > 0) return;
  stopDurationTimer();
  window.clearTimeout(disconnectReconcileTimer);
  disconnectReconcileTimer = null;
  globalDurationTimer = null;
  storedCallsStoreRef = null;
  TwilioVoiceClient.removeEventListener(
    'call:disconnected',
    handleProviderDisconnectedGlobal
  );
  RuutVoiceClient.removeEventListener(
    'call:disconnected',
    handleProviderDisconnectedGlobal
  );
  TwilioVoiceClient.removeEventListener(
    'call:reconnecting',
    handleProviderReconnectingGlobal
  );
  TwilioVoiceClient.removeEventListener(
    'call:reconnected',
    handleProviderReconnectedGlobal
  );
  RuutVoiceClient.removeEventListener(
    'call:reconnecting',
    handleProviderReconnectingGlobal
  );
  RuutVoiceClient.removeEventListener(
    'call:reconnected',
    handleProviderReconnectedGlobal
  );
  window.removeEventListener('beforeunload', handleBeforeUnloadGlobal);
  window.removeEventListener('pagehide', handlePageHideGlobal);
  document.removeEventListener(
    'visibilitychange',
    handleVisibilityChangeGlobal
  );
};

// Build the action surface used by both the root session composable and the
// lighter useCallActions consumer. All state is module-scoped — the actions
// don't depend on per-instance refs, so they're cheap to call from anywhere.
const buildCallActions = ({ callsStore, whatsappSession, t }) => {
  const findCall = callSid => callsStore.calls.find(c => c.callSid === callSid);

  const endCall = async ({ conversationId, inboxId, callSid }) => {
    const call = findCall(callSid);
    if (isWhatsappCall(call)) {
      // Pass call.callId so a wiped module state (e.g. a prior accept attempt
      // tore down the WebRTC session) doesn't stop us hitting /terminate.
      await whatsappSession.endActiveCall(call?.callId);
      stopDurationTimer();
      callTabCoordinator.release(callSid);
      callsStore.clearActiveCall();
      return;
    }

    // try/finally so a failed leaveConference (e.g. backend 5xx) still
    // tears down the local Device and UI state — otherwise the call stays
    // visually active with the mic open.
    try {
      await VoiceAPI.leaveConference({ inboxId, conversationId, callSid });
    } finally {
      // Mark dismissed so a reconcile that still sees the call server-side
      // (e.g. in the 30s rejoin-grace window) can't re-seed or auto-join a
      // call the agent explicitly ended.
      markCallDismissed(callSid);
      await voiceClientFor(call).endClientCall();
      stopDurationTimer();
      callTabCoordinator.release(callSid);
      callsStore.clearActiveCall();
      clearLocalCall(callSid);
    }
  };

  const joinCall = async ({ conversationId, inboxId, callSid }) => {
    if (globalIsJoining.value) return null;

    const call = findCall(callSid);
    if (!(await callTabCoordinator.claim(callSid))) {
      callsStore.dismissCall(callSid);
      return null;
    }
    // Outbound *WhatsApp* calls have no separate join step — the offer was
    // sent at initiate time and the answer is applied by the cable handler.
    // Routing through acceptIncomingCall here would call prepareInboundAnswer →
    // cleanup() and destroy the live outbound session. Outbound *Twilio*
    // calls still need joinConference + joinClientCall (FloatingCallWidget
    // auto-joins them), so don't short-circuit those.
    if (
      call?.callDirection === VOICE_CALL_DIRECTION.OUTBOUND &&
      isWhatsappCall(call)
    ) {
      return null;
    }

    globalIsJoining.value = true;
    try {
      if (isWhatsappCall(call)) {
        await whatsappSession.acceptIncomingCall({
          callId: call.callId,
          sdpOffer: call.sdpOffer,
          iceServers: call.iceServers,
          transport: call.transport,
        });
        callsStore.setCallActive(callSid);
        startDurationTimer(call);
        return { callId: call.callId };
      }

      const voiceClient = voiceClientFor(call);
      const device = await voiceClient.initializeDevice(inboxId);
      if (!device) return null;

      // Ring-group dials deliver the agent leg as a SIP INVITE straight to
      // this client before the agent clicks answer. Telling the backend to
      // invite the agent would fork a second, empty conference leg, so flag
      // that we already hold the leg and simply accept it below.
      const acceptExistingLeg =
        isRuutVoiceCall(call) && voiceClient.hasPendingInvitation;

      // Set BEFORE the join call lands so the account-wide voice_call.accepted
      // broadcast — which can arrive back at this same tab before this await
      // resolves — recognizes this as its own call instead of tearing it down
      // (mirrors useWhatsappCallSession's activeCallId).
      markLocalCall(callSid);

      const joinResponse = await VoiceAPI.joinConference({
        conversationId,
        inboxId,
        callSid,
        acceptExistingLeg,
      });

      await voiceClient.joinClientCall({
        to: joinResponse?.conference_sid,
        conversationId,
        callSid,
      });

      callsStore.setCallActive(callSid);
      // Outbound calls start counting when the callee answers (in-progress
      // status update), not when the agent's conference leg joins.
      if (call?.callDirection !== VOICE_CALL_DIRECTION.OUTBOUND) {
        startDurationTimer(call);
      }

      return { conferenceSid: joinResponse?.conference_sid };
    } catch (error) {
      callTabCoordinator.release(callSid);
      useAlert(error?.response?.data?.error || t('CONTACT_PANEL.CALL_FAILED'));
      if (!isWhatsappCall(call)) clearLocalCall(callSid);
      // 409 = the call already ended before accept landed (e.g. caller hung up mid-ring).
      if (error?.response?.status === 409) {
        await voiceClientFor(call).endClientCall();
        markCallDismissed(callSid);
        callsStore.dismissCall(callSid);
      } else if (!isWhatsappCall(call)) {
        // Tear down the Twilio Device on any other join error so a retry
        // starts from a clean state — joinClientCall can leave the device
        // half-initialized after a network blip.
        await voiceClientFor(call).endClientCall();
      }
      // eslint-disable-next-line no-console
      console.error('Failed to join call:', error);
      // Drop any half-built WebRTC state so the next click starts fresh.
      cleanupWhatsappSession();
      teardownDataChannelBridge();
      return null;
    } finally {
      globalIsJoining.value = false;
    }
  };

  // Await provider-side reject before dismissing the local entry; if the API
  // call fails the call should stay surfaced so the agent can retry instead of
  // disappearing while the backend still rings.
  const rejectIncomingCall = async callSid => {
    const call = findCall(callSid);
    try {
      if (isWhatsappCall(call) && call?.callId) {
        if (call.callDirection === VOICE_CALL_DIRECTION.OUTBOUND) {
          // Outbound calls that are still ringing must be terminated, not
          // rejected (reject is the inbound-side verb on Meta's API).
          await whatsappSession.endActiveCall(call.callId);
        } else {
          await whatsappSession.rejectIncomingCall(call.callId);
        }
      } else if (call?.inboxId && call?.conversationId) {
        // Twilio incoming reject: agent hasn't joined the Device yet, so
        // endClientCall is a no-op. End the conference server-side instead
        // so Twilio hangs up the inbound leg.
        await VoiceAPI.leaveConference({
          inboxId: call.inboxId,
          conversationId: call.conversationId,
          callSid,
        });
      } else {
        await voiceClientFor(call).endClientCall();
      }
    } finally {
      markCallDismissed(callSid);
      callTabCoordinator.release(callSid);
      callsStore.dismissCall(callSid);
    }
  };

  const dismissCall = callSid => {
    markCallDismissed(callSid);
    callTabCoordinator.release(callSid);
    callsStore.dismissCall(callSid);
  };

  return { endCall, joinCall, rejectIncomingCall, dismissCall };
};

const buildReactiveSurface = callsStore => {
  const activeCall = computed(() => callsStore.activeCall);
  const incomingCalls = computed(() => callsStore.incomingCalls);
  const hasActiveCall = computed(() => callsStore.hasActiveCall);
  const formattedCallDuration = computed(() => {
    const total = globalCallDuration.value;
    const minutes = Math.floor(total / 60);
    const seconds = total % 60;
    return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  });
  return {
    activeCall,
    incomingCalls,
    hasActiveCall,
    isJoining: globalIsJoiningReadonly,
    isCallTimerRunning: readonly(globalCallTimerRunning),
    formattedCallDuration,
  };
};

// Root-mount composable. Call once at the dashboard root (FloatingCallWidget
// is the natural anchor — always mounted, lifetime spans the whole session).
// This is the only path that registers global window/Twilio listeners and
// owns the duration Timer.
export function useCallSession() {
  const store = useStore();
  const callsStore = useCallsStore();
  const whatsappSession = useWhatsappCallSession();
  const { t } = useI18n();

  const reactive = buildReactiveSurface(callsStore);
  const actions = buildCallActions({ callsStore, whatsappSession, t });

  const AWAITING_REJOIN = 'awaiting_rejoin';

  const normalizeServerCall = call => ({
    callSid: call.call_sid,
    callId: call.call_id,
    provider: call.provider,
    transport: call.transport,
    status: call.status,
    callDirection: call.call_direction,
    conversationId: call.conversation_id,
    inboxId: call.inbox_id,
    caller: call.caller
      ? {
          name: call.caller.name,
          phone: call.caller.phone,
          avatar: call.caller.avatar,
          additionalAttributes: call.caller.additional_attributes || {},
        }
      : null,
    // The backend treats an agent leg disconnect as a 30s grace window
    // (status: awaiting_rejoin) before ending the call; auto-join during
    // that window re-attaches the agent's media to the existing conference.
    // An in_progress call is a fresh join or a call the agent never left —
    // we surface it as a normal in-flight call without auto-joining.
    isActive:
      call.status === VOICE_CALL_STATUS.IN_PROGRESS ||
      call.status === AWAITING_REJOIN,
    connectionState: call.status === AWAITING_REJOIN ? 'needs-join' : undefined,
  });

  // The conference controller kicks any stale agent leg on the
  // server side before the new SIP UA accepts, so auto-joining against
  // awaiting_rejoin is safe; against in_progress it could create a
  // second agent participant before the kick lands.
  const reconcileActiveCalls = async () => {
    try {
      const snapshot = await VoiceAPI.getActiveCalls();
      const previousCallSids = new Set(
        callsStore.calls.map(call => call.callSid)
      );
      const serverCalls = (snapshot || [])
        .map(normalizeServerCall)
        .filter(call => !callTabCoordinator.isOwnedElsewhere(call.callSid))
        .filter(call => !dismissedCallSids.has(call.callSid));

      callsStore.reconcileServerCalls(serverCalls);
      const serverCallSids = new Set(serverCalls.map(call => call.callSid));
      previousCallSids.forEach(callSid => {
        if (!serverCallSids.has(callSid)) callTabCoordinator.release(callSid);
      });

      const awaiting = serverCalls.find(
        call => call.status === AWAITING_REJOIN
      );
      if (!awaiting) return;
      if (globalIsJoining.value) return;
      const client = voiceClientFor({
        provider: awaiting.provider,
      });
      if (client.hasActiveConnection) return;

      await actions.joinCall({
        conversationId: awaiting.conversationId,
        inboxId: awaiting.inboxId,
        callSid: awaiting.callSid,
      });
    } catch (error) {
      // Reconciliation is best-effort. Cable events remain the live path and
      // the next visibility or provider-state change retries the snapshot.
    }
  };

  const handleClaimedElsewhere = event => {
    const { callSid } = event.detail;
    const call = callsStore.calls.find(item => item.callSid === callSid);
    if (!call) return;
    if (call.isActive) voiceClientFor(call).endClientCall();
    callsStore.dismissCall(callSid);
  };

  // Cable broadcasts (voice_call.incoming / message.created) are one-shot, so
  // on a hard refresh they leave the calls store empty. Seed it from any
  // ringing voice_call message in the conversation cache. Skip calls the
  // agent has already dismissed locally so they don't re-pop on the next
  // conversation update.
  const seedCallsFromHydratedMessages = () => {
    const conversations = store.getters.getAllConversations || [];
    const currentUserId = store.getters.getCurrentUserID;
    const currentUserAvailability = store.getters.getCurrentUserAvailability;
    conversations.forEach(conv => {
      (conv.messages || []).forEach(msg => {
        if (msg.content_type !== CONTENT_TYPES.VOICE_CALL) return;
        if (msg.call?.status !== VOICE_CALL_STATUS.RINGING) return;
        const callSid = msg.call?.provider_call_id;
        if (callSid && dismissedCallSids.has(callSid)) return;
        handleVoiceCallCreated(msg, currentUserId, currentUserAvailability);
      });
    });
  };

  watch(
    () => [reactive.hasActiveCall.value, reactive.activeCall.value?.status],
    ([active, status]) => {
      if (!active) {
        stopDurationTimer();
        return;
      }
      // The callee's answer arrives as an in-progress status update; inbound
      // timers already started at join are kept (guarded inside
      // startDurationTimer) so a late update can't reset elapsed time.
      if (status === VOICE_CALL_STATUS.IN_PROGRESS) {
        const call = reactive.activeCall.value;
        startDurationTimer(call);
        if (
          isWhatsappCall(call) &&
          call.callDirection === VOICE_CALL_DIRECTION.OUTBOUND
        ) {
          armOutboundRecorder();
        }
      }
    },
    { immediate: true }
  );

  onMounted(() => {
    attachGlobalsOnFirstMount(callsStore);
    reconcileActiveCallsRef = reconcileActiveCalls;
    callTabCoordinator.addEventListener(
      'call:claimed-elsewhere',
      handleClaimedElsewhere
    );
    seedCallsFromHydratedMessages();
    reconcileActiveCalls();
  });

  // Re-seed when conversations stream in after mount; addCall merges by callSid
  // and dismissed sids are filtered, so this is idempotent.
  watch(
    () => store.getters.getAllConversations?.length,
    () => seedCallsFromHydratedMessages()
  );

  onUnmounted(() => {
    reconcileActiveCallsRef = null;
    callTabCoordinator.removeEventListener(
      'call:claimed-elsewhere',
      handleClaimedElsewhere
    );
    callTabCoordinator.releaseAll();
    detachGlobalsOnLastUnmount();
  });

  return { ...reactive, ...actions };
}

// Lightweight consumer for components that need to read state and trigger
// actions but should NOT mount global listeners (e.g., per-message bubbles
// rendered in a thread). Reads from the same module-level state that
// useCallSession owns, so the duration timer and dismissed set stay coherent.
export function useCallActions() {
  const callsStore = useCallsStore();
  const whatsappSession = useWhatsappCallSession();
  const { t } = useI18n();

  const reactive = buildReactiveSurface(callsStore);
  const actions = buildCallActions({ callsStore, whatsappSession, t });

  return { ...reactive, ...actions };
}
